import { Box, Typography } from "@mui/material"
import React, { useCallback, useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { customPalette } from "../../../../../../../../../theme"
import {
  BOTTOM_NAVIGATION_HEIGHT,
  CustomBottomNavigation,
} from "../../../../../../../../components/buttons/navigate-button/CustomBottomNavigation"
import { BSIfcDisplayer } from "../../../../../../../../components/ifc-displayer/BSIfcDisplayer"
import { ViewerTypes } from "../../../../../../../../components/ifc-displayer/enums/ViewerTypes"
import { SuccessContext } from "../../../../../../../../components/layout/success-snackbar"
import { pagesUrl } from "../../../../../../../../core/appConstants"
import { BSModelFileContext } from "../../../../../../../../core/context/beem-shot/BSBimModel/BSBimModelFileContext"
import { BSVariantContext } from "../../../../../../../../core/context/beem-shot/BSVariant/BSVariantContext"
import { CodeReferenceContext } from "../../../../../../../../core/context/code-acv/CodeReferenceContext"
import { ProjectContext } from "../../../../../../../../core/context/project/ProjectContext"
import { CodeExtrait } from "../../../../../../../../core/dto/code-extrait/code-extrait"
import { CodeExtraitDisplay } from "../../../../../../../../core/dto/code-extrait/CodeExtraitDisplay"
import { BSBuildingMorphologyEnum } from "../../../../../../../../core/enum/bsBuildingMorphologyEnum"
import { CalculStatusEnum } from "../../../../../../../../core/enum/calculStatusEnum"
import { useForm } from "../../../../../../../../core/hooks/form/use-form"
import { nonZero, required, ValidationRule } from "../../../../../../../../core/hooks/form/validation"
import { useViewerAnalyze } from "../../../../../../../../core/hooks/viewer/useViewerAnalyze"
import { resolveUrl } from "../../../../../../../../core/services/http-service"
import { BSBuildingContext } from "../../../building/components/context/BSBuildingContext"
import { BSVariantControlPanelContext } from "../../../components/BSVariantControlPanelContext"
import { ControlPanelTabHeader } from "../../../components/ControlPanelTabHeader"
import {
  bsBuildingMorphologyDtoToForm,
  BSBuildingMorphologyForm,
  bsBuildingMorphologyFormToCreationDto,
  bsBuildingMorphologyFormToUpdateDto,
  IBSBuildingMorphologyForm,
} from "./components/BSBuildingMorphologyForm"
import { BSBuildingMorphologyContext } from "./context/BSBuildingMorphologyContext"
import { BSBimModelContext } from "../../../../../../../../core/context/beem-shot/BSBimModel/BSBimModelContext"

interface IProps {
  isUpdating: boolean
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>
}

export function BSBuildingMorphoTab({ isUpdating, setIsUpdating }: Readonly<IProps>): React.JSX.Element {
  const navigate = useNavigate()

  const { isCodeAcvLoading } = useContext(CodeReferenceContext)
  const { bsBimModel } = useContext(BSBimModelContext)
  const { file } = useContext(BSModelFileContext)
  const { controlPanelRefWidth } = useContext(BSVariantControlPanelContext)
  const { project } = useContext(ProjectContext)
  const { selectedVariant } = useContext(BSVariantContext)
  const { selectedBSBuilding, updateCalculStatusState } = useContext(BSBuildingContext)
  const { bsBuildingMorphology, updateBuildingMorphology, createNewBSBuildingMorphology } =
    useContext(BSBuildingMorphologyContext)
  const openSuccessSnackbar = useContext(SuccessContext)

  const [codesExtraits, setCodesExtraits] = useState<CodeExtrait[]>([])
  const [selectedCodeExtrait, setSelectedCodeExtrait] = useState<CodeExtraitDisplay>()

  const { handleClickAcv, onChangeModel, setProgress } = useViewerAnalyze(
    codesExtraits,
    setCodesExtraits,
    selectedCodeExtrait,
    setSelectedCodeExtrait
  )

  const submit: (form: IBSBuildingMorphologyForm) => Promise<any> = useCallback(
    (form) => {
      if (project?.id && selectedVariant?.id && selectedBSBuilding?.id && !bsBuildingMorphology?.id) {
        return createNewBSBuildingMorphology(bsBuildingMorphologyFormToCreationDto(form, selectedBSBuilding.id)).then(() => {
          setIsUpdating(false)
          openSuccessSnackbar("Les modifications ont bien été prises en compte.")
        })
      } else if (selectedBSBuilding?.id && bsBuildingMorphology?.id) {
        return updateBuildingMorphology(
          bsBuildingMorphologyFormToUpdateDto(form, selectedBSBuilding.id, bsBuildingMorphology.id)
        ).then(() => {
          setIsUpdating(false)
          updateCalculStatusState(CalculStatusEnum.WAITING_FOR_CONFIGURATION)
          openSuccessSnackbar("Les modifications ont bien été prises en compte.")
        })
      }
      return Promise.resolve()
    },
    [
      bsBuildingMorphology?.id,
      createNewBSBuildingMorphology,
      openSuccessSnackbar,
      project?.id,
      selectedBSBuilding?.id,
      selectedVariant?.id,
      setIsUpdating,
      updateBuildingMorphology,
      updateCalculStatusState,
    ]
  )

  const { form, errors, handleChange, handleSubmit, isSubmitting, setRules } = useForm(
    bsBuildingMorphology,
    bsBuildingMorphologyDtoToForm,
    [],
    submit,
    () => setIsUpdating(true)
  )

  useEffect(() => {
    const rules: ValidationRule<IBSBuildingMorphologyForm>[] = []
    if (form.shape === BSBuildingMorphologyEnum.L) {
      rules.push(
        ...[
          required("shape"),
          required("dimensionL1"),
          required("dimensionL2"),
          required("dimensionL3"),
          required("dimensionL4"),
          nonZero("dimensionL1"),
          nonZero("dimensionL2"),
          nonZero("dimensionL3"),
          nonZero("dimensionL4"),
        ]
      )
    }

    if (form.shape === BSBuildingMorphologyEnum.RECTANGLE) {
      rules.push(
        ...[
          required("shape"),
          required("dimensionRectangle1"),
          required("dimensionRectangle2"),
          nonZero("dimensionRectangle1"),
          nonZero("dimensionRectangle2"),
        ]
      )

      if (form.murRideauVitrage) {
        rules.push(...[required("ratioMursRideauxVitrage"), nonZero("ratioMursRideauxVitrage")])
      }

      if (form.verrieresToiture) {
        rules.push(...[required("ratioVerrieresToiture"), nonZero("ratioVerrieresToiture")])
      }
    }
    setRules(rules)
  }, [form.murRideauVitrage, form.shape, form.verrieresToiture, setRules])

  return (
    <Box id="model-container" px={5} pb={2} sx={{ overflow: "auto" }}>
      <ControlPanelTabHeader title={selectedBSBuilding?.bim ? "Maquette numérique" : "Morphologie"} />
      {selectedBSBuilding?.bim && (
        <Box>
          {!isCodeAcvLoading && file ? (
            <BSIfcDisplayer
              file={file}
              onChangeModel={onChangeModel}
              setExternalProgress={setProgress}
              type={ViewerTypes.CODE_VERIFICATION}
              codesExtraits={codesExtraits}
              height={`calc(100vh - ${BOTTOM_NAVIGATION_HEIGHT} -  200px)`}
              setSelectedCodeExtrait={handleClickAcv}
              backgroundColor={customPalette.paperBackground}
            />
          ) : (
            <Typography color="textSecondary">Aucune maquette n'a été importée pour l'instant</Typography>
          )}

          <CustomBottomNavigation
            actionLabel={bsBimModel ? "Modifier la maquette" : "Ajouter une maquette"}
            validateAction={() =>
              navigate(
                resolveUrl(pagesUrl.BUILDING_CHOICE, [project?.id, selectedVariant?.id, selectedBSBuilding?.id], {
                  from: "TAB",
                })
              )
            }
            additionalLeftPadding={controlPanelRefWidth ?? 0}
          />
        </Box>
      )}

      {!selectedBSBuilding?.bim && (
        <Box
          component="form"
          sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
          id="update-morphology-form"
          onSubmit={handleSubmit}>
          <BSBuildingMorphologyForm form={form} errors={errors} handleChange={handleChange} />
          {isUpdating && (
            <CustomBottomNavigation
              actionLabel="Enregistrer"
              isSubmitting={isSubmitting}
              formId="update-morphology-form"
              additionalLeftPadding={controlPanelRefWidth ?? 0}
            />
          )}
        </Box>
      )}
    </Box>
  )
}
